// AI-CONTEXT: See docs/features/participation-ladder.md
// AI-CONTEXT: See docs/features/participatieladder-2026-3.md
import defaultParticipationParameters from './defaultParticipationParameters'
import calculateParticipationState from './calculateParticipationState'
import calculateParticipationDelta from './calculateParticipationDelta'
import calculateParticipationPosts from './calculateParticipationPosts'
import deriveParticipationStep from './deriveParticipationStep'
import getSituationLabel from './getSituationLabel'
import ladderSteps from './ladderSteps'
import attributedValue from '../msiMonetisationHelpers/applyRekenregels'

/**
 * Het rekenmodel volgt uit de vastgezette parameterset, niet uit een
 * versienummer: een set met `posts` (2026.3 en later) rekent per stakeholder
 * en post, een set zonder (2026.1/2026.2) met de vier onderdelen van toen.
 * @param {object} parameters
 * @returns {boolean}
 */
const isPostsModel = (parameters) => Array.isArray(parameters && parameters.posts)

const withLadder = (profile, situation) => ({
    situationLabel: getSituationLabel(profile),
    ladderStep: deriveParticipationStep(profile),
    ...situation,
})

/**
 * Top-level entry point: computes the T0 and T1 states plus their delta.
 *
 * Beide modellen geven `delta.totalSocietalValue`, `governmentBalance`,
 * `personalIncomeChange` en `ladderStepChange`, zodat afnemers die alleen
 * totalen lezen niets hoeven te weten van het model. `model` zegt welk het is;
 * alleen 'posts' heeft `posts`, `perStakeholder`, `perType` en `notices`.
 *
 * @param {object} input
 * @param {object} [input.participantProfileT0]
 * @param {object} [input.participantProfileT1]
 * @param {object} [input.parameters]
 * @param {{ birthDate?: string, t0Date?: *, t1Date?: * }} [input.context] - Leeftijdsovergang (alleen model 'posts')
 * @param {number|null} [input.horizon] - Tijdshorizon van de MSI-rekenregels (resolveInterventionDose).
 *   Met een horizon komt `delta.attributedSocietalValue` (× impactclaim × factor) naast het
 *   bruto `totalSocietalValue`; zonder is die null — niet doorgerekend, nooit 0.
 */
const calculateParticipationImpact = ({
    participantProfileT0 = {},
    participantProfileT1 = {},
    parameters = defaultParticipationParameters,
    context,
    horizon = null,
} = {}) => {
    if (isPostsModel(parameters)) {
        const result = calculateParticipationPosts({
            profileT0: participantProfileT0,
            profileT1: participantProfileT1,
            parameters,
            context,
            horizon,
        })
        const t0 = withLadder(participantProfileT0, result.t0)
        const t1 = withLadder(participantProfileT1, result.t1)
        return {
            model: 'posts',
            t0,
            t1,
            delta: {
                totalSocietalValue: result.totals.reeleWaarde,
                governmentBalance: result.totals.governmentBalance,
                personalIncomeChange: result.totals.personalIncomeChange,
                ladderStepChange: ladderSteps(t0.ladderStep, t1.ladderStep).change,
                horizon,
                attributedSocietalValue: result.attributedTotals ? result.attributedTotals.reeleWaarde : null,
            },
            posts: result.posts,
            perStakeholder: result.perStakeholder,
            perType: result.perType,
            notices: result.notices,
        }
    }

    const t0 = calculateParticipationState(participantProfileT0, parameters)
    const t1 = calculateParticipationState(participantProfileT1, parameters)
    const delta = calculateParticipationDelta(t0, t1)
    return {
        model: 'legacy',
        t0,
        t1,
        delta: { ...delta, horizon, attributedSocietalValue: attributedValue(delta.totalSocietalValue, horizon) },
    }
}

export default calculateParticipationImpact
export { isPostsModel }
